/* global module */

/**
 * Contact form with inquiry-type routing (MFS-13).
 *
 * Swaps field groups by inquiry type and posts JSON to the Cloudflare Worker, which
 * routes the notification email and stores the submission in Shopify.
 */
(function () {
  /**
   * Every key is always sent, empty string when blank: the Worker feeds
   * these straight into Shopify GraphQL, which rejects null but accepts "".
   */
  var PAYLOAD_DEFAULTS = {
    inquiry_type: "",
    subject: "",
    name: "",
    email: "",
    newsletter: false,
    page: "",
    locale: "",
    submitted_at: "",
    website: "",
    turnstile_token: "",
    attachment_filename: "",
    attachment_mime: "",
    attachment_content_base64: "",
    company_name: "",
    project_nature: "",
    project_location: "",
    vision: "",
    core_expertise_json: "[]",
    why_moment_factory: "",
    relationship_type: "",
    media_outlet: "",
    request_scope_json: "[]",
    message: "",
  };

  // `website` (honeypot) IS sent now, so the server can reject bots that POST
  // directly; the client still short-circuits on it below. Turnstile's injected
  // `cf-turnstile-response` is read explicitly into turnstile_token, not here.
  var SKIP_FIELDS = ["terms", "attachment", "cf-turnstile-response"];

  // Matches the longest pane transition in nova-contact-form-routing.css.
  var TRANSITION_MS = 420;

  // Keeps a little breathing room below a sticky form instead of allowing its
  // final control to sit directly against the viewport edge.
  var STICKY_VIEWPORT_GAP = 24;

  /**
   * Mirrors MAX_ATTACHMENT_BYTES in worker/src/index.js -- keep the two in sync.
   * The file rides base64-encoded, which inflates it by about a third, so 3.5 MB
   * raw (~4.7 MB encoded) stays well inside the request limit with room for the
   * fields.
   *
   * Shopify Files accepts 20 MB and Resend 40 MB, so if a bigger limit is ever
   * needed the file should skip the JSON body entirely: mint a staged upload
   * target, PUT the file straight to it from here, and send only the resourceUrl.
   * The Worker would then have to reject any resourceUrl outside
   * shopify-staged-uploads.storage.googleapis.com, since it becomes client-supplied.
   */
  var MAX_FILE_BYTES = 3.5 * 1024 * 1024;

  var ALLOWED_FILE_TYPES = {
    "application/pdf": ".pdf",
    "application/msword": ".doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
    "application/vnd.ms-powerpoint": ".ppt",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": ".pptx",
    "application/vnd.oasis.opendocument.text": ".odt",
    "application/rtf": ".rtf",
    "text/rtf": ".rtf",
    "text/plain": ".txt",
  };

  // Browsers disagree on MIME for Office files, so extension is the fallback check.
  var ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx", ".ppt", ".pptx", ".odt", ".rtf", ".txt"];

  function formatMegabytes(bytes) {
    return (bytes / 1024 / 1024).toFixed(1).replace(/\.0$/, "") + " MB";
  }

  function fileExtension(name) {
    var dot = name.lastIndexOf(".");
    return dot === -1 ? "" : name.slice(dot).toLowerCase();
  }

  function formFitsViewport(contentHeight, viewportHeight, stickyOffset) {
    return contentHeight + stickyOffset + STICKY_VIEWPORT_GAP <= viewportHeight;
  }

  function stickyHeaderHeight() {
    var header = document.querySelector && document.querySelector("#shopify-section-nova-header .nova-header");
    var hasStickyHeader =
      header &&
      header.classList &&
      (header.classList.contains("stick_on_scroll") || header.classList.contains("always_stick"));
    if (!hasStickyHeader) return 0;
    if (!window.getComputedStyle) return 64;
    var rootStyles = window.getComputedStyle(document.documentElement);
    return parseFloat(rootStyles.getPropertyValue("--header-height")) || 64;
  }

  function NovaContactRouting(form) {
    this.form = form;
    this.webhookUrl = form.dataset.webhookUrl;
    // Localized copy rendered by the snippet from locale keys (English fallback
    // so the form still works if the attributes are ever missing). File-error
    // strings carry [extensions]/[size]/[limit] tokens filled at runtime.
    this.messages = {
      error: form.dataset.msgError || "Sorry, your message could not be sent. Please try again or email us directly.",
      success: form.dataset.msgSuccess || "Thanks! Your message is on its way.",
      turnstile: form.dataset.msgTurnstile || "Please complete the verification challenge, then send again.",
      sending: form.dataset.msgSending || "Sending…",
      fileType: form.dataset.msgFileType || "That file type is not accepted. Please use [extensions].",
      fileSize: form.dataset.msgFileSize || "That file is [size]. The limit is [limit].",
      required: form.dataset.msgRequired || "",
      requiredSelect: form.dataset.msgRequiredSelect || "",
      requiredCheckbox: form.dataset.msgRequiredCheckbox || "",
      email: form.dataset.msgEmail || "",
    };
    this.typeSelect = form.querySelector("[data-inquiry-type]");
    this.groups = Array.prototype.slice.call(form.querySelectorAll("[data-inquiry-group]"));
    this.status = form.querySelector("[data-form-status]");
    this.submitButton = form.querySelector("[data-submit-button]");
    this.terms = form.querySelector("[data-terms]");
    this.shell = form.closest("[data-form-shell]");
    this.thanks = this.shell && this.shell.querySelector("[data-thanks]");
    this.thanksHeading = this.shell && this.shell.querySelector("[data-thanks-heading]");
    this.sendAnother = this.shell && this.shell.querySelector("[data-send-another]");
    this.formContent = form.closest(".nova-contact-form-with-image-hotspots__form-content");

    this.onTypeChange = this.onTypeChange.bind(this);
    this.onTermsChange = this.onTermsChange.bind(this);
    this.onFileChange = this.onFileChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.showForm = this.showForm.bind(this);
    this.syncStickyFit = this.syncStickyFit.bind(this);

    this.typeSelect.addEventListener("change", this.onTypeChange);
    if (this.terms) this.terms.addEventListener("change", this.onTermsChange);
    if (this.sendAnother) this.sendAnother.addEventListener("click", this.showForm);
    Array.prototype.forEach.call(
      form.querySelectorAll("[data-attachment]"),
      function (input) {
        input.addEventListener("change", this.onFileChange);
      },
      this,
    );
    this.form.addEventListener("submit", this.onSubmit);

    this.localizeValidation();
    this.showGroup(this.typeSelect.value);
    this.syncTermsGate();
    this.observeStickyFit();
  }

  NovaContactRouting.prototype.syncStickyFit = function () {
    if (!this.formContent) return;

    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    var contentHeight = this.formContent.scrollHeight;
    var overflowsViewport = !formFitsViewport(contentHeight, viewportHeight, stickyHeaderHeight());
    this.formContent.toggleAttribute("data-contact-form-overflowing", overflowsViewport);

    if (overflowsViewport) {
      this.formContent.style.setProperty(
        "--contact-form-overflow-top",
        viewportHeight - contentHeight - STICKY_VIEWPORT_GAP + "px",
      );
    } else {
      this.formContent.style.removeProperty("--contact-form-overflow-top");
    }
  };

  NovaContactRouting.prototype.observeStickyFit = function () {
    if (!this.formContent) return;

    window.addEventListener("resize", this.syncStickyFit);
    if (typeof window.ResizeObserver === "function") {
      this.stickyResizeObserver = new window.ResizeObserver(this.syncStickyFit);
      this.stickyResizeObserver.observe(this.formContent);
    }
    this.syncStickyFit();
  };

  NovaContactRouting.prototype.destroy = function () {
    window.removeEventListener("resize", this.syncStickyFit);
    if (this.stickyResizeObserver) this.stickyResizeObserver.disconnect();
    window.clearTimeout(this.hideThanksTimer);
  };

  /**
   * The browser writes its own validation bubbles ("Please fill out this field")
   * in the BROWSER's UI language, which has nothing to do with the page's -- a
   * French visitor with an English browser reads English on the French site.
   * Overriding the message with setCustomValidity is the only way to control it.
   *
   * `invalid` does not bubble, so the listener is registered in the capture
   * phase. The message is cleared again on input/change: a custom validity string
   * makes the field invalid until it is reset, which would otherwise block the
   * form even after the visitor fixes the value.
   */
  NovaContactRouting.prototype.localizeValidation = function () {
    var self = this;

    this.form.addEventListener(
      "invalid",
      function (event) {
        var message = self.validationMessageFor(event.target);
        if (message) event.target.setCustomValidity(message);
      },
      true,
    );

    var clear = function (event) {
      var field = event.target;
      if (field && typeof field.setCustomValidity === "function") field.setCustomValidity("");
    };
    this.form.addEventListener("input", clear);
    this.form.addEventListener("change", clear);
  };

  /** Translated stand-in for a field's native validation message, or "" to keep the browser's. */
  NovaContactRouting.prototype.validationMessageFor = function (field) {
    var validity = field && field.validity;
    if (!validity) return "";

    if (validity.valueMissing) {
      if (field.tagName === "SELECT") return this.messages.requiredSelect;
      if (field.type === "checkbox" || field.type === "radio") return this.messages.requiredCheckbox;
      return this.messages.required;
    }
    // type="email" rejects a malformed address with its own wording too.
    if (validity.typeMismatch && field.type === "email") return this.messages.email;

    return "";
  };

  /**
   * Contact name, email and subject are repeated per group, so a swap would
   * otherwise discard whatever the visitor already typed.
   */
  NovaContactRouting.prototype.carryOverValues = function (nextType) {
    var current = this.form.querySelector("[data-inquiry-group]:not([hidden])");
    var next = this.form.querySelector('[data-inquiry-group="' + nextType + '"]');
    if (!current || !next || current === next) return;

    Array.prototype.forEach.call(current.querySelectorAll("[data-carry-over]"), function (field) {
      var twin = next.querySelector('[data-carry-over][name="' + field.name + '"]');
      // Assign even when empty: a visitor who clears a carried field and then
      // switches type must not have the old value restored in the next group.
      if (!twin) return;
      twin.value = field.value;
      // Assigning .value fires no input/change, so a translated "required"
      // message set earlier would survive onto a now-filled field and block
      // submission with a bubble that contradicts what the visitor sees.
      twin.setCustomValidity("");
    });
  };

  /**
   * The Send button stays disabled until the terms box is ticked. `required` on
   * the checkbox still backs this up if the script fails to load.
   */
  NovaContactRouting.prototype.syncTermsGate = function () {
    if (!this.terms || !this.submitButton) return;
    this.submitButton.disabled = !this.terms.checked;
  };

  NovaContactRouting.prototype.onTermsChange = function () {
    this.syncTermsGate();
  };

  /**
   * form.reset() on its own reverts the inquiry-type select without re-running
   * showGroup, so the previous group's fields would stay on screen underneath a
   * select that reads "General Information".
   */
  NovaContactRouting.prototype.resetForm = function () {
    this.form.reset();
    this.showGroup(this.typeSelect.value);
    // form.reset() restores values but leaves any custom validity string in
    // place, which would keep a reset field invalid on the next submit.
    Array.prototype.forEach.call(this.form.elements, function (field) {
      if (typeof field.setCustomValidity === "function") field.setCustomValidity("");
    });
    // Clear any lingering file-rejection message + custom validity, so it does
    // not persist under an empty input after "Send another" or a group switch.
    Array.prototype.forEach.call(this.form.querySelectorAll("[data-attachment]"), function (input) {
      input.setCustomValidity("");
    });
    Array.prototype.forEach.call(this.form.querySelectorAll("[data-file-error]"), function (error) {
      error.textContent = "";
      error.hidden = true;
    });
    this.syncTermsGate();
  };

  /**
   * Swaps the form for the thank-you panel. Both share one grid cell and the
   * outgoing pane keeps its box, so nothing below the form moves.
   *
   * Revealing is two steps: drop `hidden`, force a reflow, then add the state
   * class. Without the reflow the browser resolves both in one pass and the
   * entry transition never runs.
   *
   * Falls back to the inline status line if the panel is missing, so an older
   * cached snippet still confirms the send.
   */
  NovaContactRouting.prototype.showThanks = function () {
    if (!this.shell || !this.thanks) {
      this.showStatus(this.messages.success, false);
      return;
    }

    this.hideStatus();
    this.thanks.hidden = false;
    void this.thanks.offsetHeight;
    this.shell.classList.add("nova-contact-routing__shell--thanks");
    this.resetForm();

    // Without moving focus a screen reader announces nothing, and on mobile the
    // browser never scrolls the message into view.
    if (this.thanksHeading) this.thanksHeading.focus();
  };

  NovaContactRouting.prototype.showForm = function () {
    if (!this.shell || !this.thanks) return;

    this.shell.classList.remove("nova-contact-routing__shell--thanks");

    // Re-hide only once the fade has finished, otherwise it vanishes instantly.
    var thanks = this.thanks;
    window.clearTimeout(this.hideThanksTimer);
    this.hideThanksTimer = window.setTimeout(function () {
      thanks.hidden = true;
    }, TRANSITION_MS);

    // Flush the class change before focusing: a control still resolving as
    // visibility:hidden cannot take focus, and the failure is silent.
    void this.form.offsetHeight;
    this.typeSelect.focus();
  };

  /**
   * Rejects the file in the browser rather than letting it fail deeper in the
   * pipeline, where a submitter would get a success message either way. This is
   * a usability guard, not a security control: the endpoint is public, so the Worker
   * has to enforce the same limits on its side.
   */
  NovaContactRouting.prototype.validateFile = function (input) {
    var error = input.parentNode.querySelector("[data-file-error]");
    var file = input.files && input.files[0];
    var message = "";

    if (file) {
      var extension = fileExtension(file.name);
      var typeAllowed =
        Object.prototype.hasOwnProperty.call(ALLOWED_FILE_TYPES, file.type) ||
        ALLOWED_EXTENSIONS.indexOf(extension) !== -1;

      if (!typeAllowed) {
        message = this.messages.fileType.replace("[extensions]", ALLOWED_EXTENSIONS.join(", "));
      } else if (file.size > MAX_FILE_BYTES) {
        message = this.messages.fileSize
          .replace("[size]", formatMegabytes(file.size))
          .replace("[limit]", formatMegabytes(MAX_FILE_BYTES));
      }
    }

    // On rejection the bad file is cleared, so the (optional) input is now empty
    // and must NOT stay invalid — clearing programmatically fires no `change`,
    // which would otherwise strand submission. Keep the inline error visible so
    // the visitor still sees why nothing attached; never set custom validity.
    if (message) input.value = "";
    input.setCustomValidity("");
    if (error) {
      error.textContent = message;
      error.hidden = !message;
    }
    return !message;
  };

  NovaContactRouting.prototype.onFileChange = function (event) {
    this.validateFile(event.target);
  };

  /**
   * Hidden groups are disabled as well as hidden: a disabled field is skipped by
   * native validation and never reaches the payload, so a required field in a
   * group the visitor cannot see can't block submission.
   */
  NovaContactRouting.prototype.showGroup = function (inquiryType) {
    this.groups.forEach(function (group) {
      var isActive = group.dataset.inquiryGroup === inquiryType;
      group.hidden = !isActive;
      Array.prototype.forEach.call(group.querySelectorAll("input, select, textarea"), function (field) {
        field.disabled = !isActive;
      });
    });
    this.syncStickyFit();
  };

  NovaContactRouting.prototype.onTypeChange = function (event) {
    this.carryOverValues(event.target.value);
    this.showGroup(event.target.value);
    this.hideStatus();
  };

  NovaContactRouting.prototype.readValue = function (field, payload) {
    var name = field.name;
    if (!name || SKIP_FIELDS.indexOf(name) !== -1) return;

    if (field.type === "checkbox") {
      payload[name] = field.checked;
      return;
    }

    if (field.type === "radio") {
      if (field.checked) payload[name] = field.value;
      return;
    }

    var value = field.value.trim();

    // Shopify stores these as list fields, which take a JSON array string.
    if (field.hasAttribute("data-json-list")) {
      payload[name + "_json"] = value ? JSON.stringify([value]) : "[]";
      return;
    }

    payload[name] = value;
  };

  /**
   * Reads the file as base64 for the JSON payload. The Worker decodes it back to
   * binary with toBinary(value; base64) before handing it to Shopify.
   */
  function readFileAsBase64(file) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onerror = function () {
        reject(new Error("Could not read the selected file."));
      };
      reader.onload = function () {
        // strip the "data:<mime>;base64," prefix -- the Worker wants the payload only
        var result = String(reader.result);
        resolve(result.slice(result.indexOf(",") + 1));
      };
      reader.readAsDataURL(file);
    });
  }

  NovaContactRouting.prototype.buildPayload = function () {
    var payload = Object.assign({}, PAYLOAD_DEFAULTS);
    var self = this;

    Array.prototype.forEach.call(this.form.elements, function (field) {
      if (field.disabled) return;
      self.readValue(field, payload);
    });

    payload.page = window.location.href;
    // Rendered by the snippet from request.locale; the <html lang> attribute is
    // the fallback so the language still rides along if the attribute is absent.
    payload.locale = this.form.dataset.locale || document.documentElement.lang || "";
    payload.submitted_at = new Date().toISOString();

    // Turnstile injects this hidden input when a widget is configured; empty
    // string when there is no widget. The Worker verifies it server-side.
    var turnstile = this.form.querySelector('[name="cf-turnstile-response"]');
    payload.turnstile_token = turnstile ? turnstile.value : "";

    var attachment = this.form.querySelector("[data-attachment]:not([disabled])");
    var file = attachment && attachment.files && attachment.files[0];
    if (!file) return Promise.resolve(payload);

    payload.attachment_filename = file.name;
    payload.attachment_mime = file.type || "application/octet-stream";
    return readFileAsBase64(file).then(function (base64) {
      payload.attachment_content_base64 = base64;
      return payload;
    });
  };

  NovaContactRouting.prototype.showStatus = function (message, isError) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.hidden = false;
    this.status.classList.toggle("nova-contact-routing__status--error", Boolean(isError));
    this.status.classList.toggle("nova-contact-routing__status--success", !isError);
    this.status.focus();
  };

  NovaContactRouting.prototype.hideStatus = function () {
    if (this.status) this.status.hidden = true;
  };

  NovaContactRouting.prototype.setBusy = function (isBusy) {
    this.form.setAttribute("aria-busy", isBusy ? "true" : "false");
    if (!this.submitButton) return;
    // Remember the button's own label the first time, so it can be restored.
    if (this.submitLabel == null) this.submitLabel = this.submitButton.textContent.trim();
    if (isBusy) {
      // Visible feedback while the pipeline runs (upload/store/email take ~1-2s):
      // spinner via the --busy class + a "Sending…" label.
      this.submitButton.disabled = true;
      this.submitButton.classList.add("nova-contact-routing__button--busy");
      this.submitButton.textContent = this.messages.sending;
    } else {
      this.submitButton.classList.remove("nova-contact-routing__button--busy");
      this.submitButton.textContent = this.submitLabel;
      // Re-enabling defers to the terms gate: a reset form has an unticked box.
      this.syncTermsGate();
    }
  };

  NovaContactRouting.prototype.onSubmit = function (event) {
    event.preventDefault();

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    // Honeypot: a bot filled a field no human can see. Look successful, send nothing.
    var honeypot = this.form.querySelector('[name="website"]');
    if (honeypot && honeypot.value) {
      this.showThanks();
      return;
    }

    // If a Turnstile widget is configured but has not produced a token yet
    // (still loading, unsolved, expired), tell the visitor rather than posting
    // an empty token and surfacing only a generic failure.
    var turnstileWidget = this.form.querySelector(".cf-turnstile");
    var turnstileToken = this.form.querySelector('[name="cf-turnstile-response"]');
    if (turnstileWidget && !(turnstileToken && turnstileToken.value)) {
      this.showStatus(this.messages.turnstile, true);
      return;
    }

    var self = this;
    this.setBusy(true);
    this.hideStatus();

    this.buildPayload()
      .then(function (payload) {
        return fetch(self.webhookUrl, {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify(payload),
        });
      })
      .then(function (response) {
        if (!response.ok) throw new Error("Webhook responded " + response.status);
        self.showThanks();
      })
      .catch(function () {
        self.showStatus(self.messages.error, true);
      })
      .finally(function () {
        self.setBusy(false);
        // A Turnstile token is single-use. Reset the widget so "Send another"
        // and post-error retries mint a fresh one instead of reusing a spent
        // token (which the Worker rejects). No-op when no widget is configured.
        // The empty-token case is already guarded in onSubmit before this point.
        var widget = self.form.querySelector(".cf-turnstile");
        if (widget && window.turnstile && typeof window.turnstile.reset === "function") {
          try {
            window.turnstile.reset(widget);
          } catch (e) {
            /* widget not yet rendered — nothing to reset */
          }
        }
      });
  };

  /**
   * Load Cloudflare's api.js once, in EXPLICIT mode (no auto-render), invoking
   * the callback when window.turnstile is ready. Explicit mode is what lets us
   * render reliably both on first load and after the theme editor re-injects the
   * section (a re-injected <script> tag never executes, so we must own loading).
   */
  function ensureTurnstileApi(cb) {
    if (window.turnstile && typeof window.turnstile.render === "function") {
      cb();
      return;
    }
    var existing = document.querySelector("script[data-mf-turnstile-api]");
    if (existing) {
      existing.addEventListener("load", cb);
      return;
    }
    var s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.setAttribute("data-mf-turnstile-api", "");
    s.addEventListener("load", cb);
    document.head.appendChild(s);
  }

  /**
   * The language Cloudflare should render its own widget copy in. Turnstile
   * takes an ISO 639-1 code (optionally with a region) or "auto"; anything else
   * is rejected, so an unexpected value falls back to Cloudflare's detection.
   */
  function turnstileLanguage(el) {
    var lang = (el.dataset.language || document.documentElement.lang || "").toLowerCase();
    return /^[a-z]{2}(-[a-z]{2})?$/.test(lang) ? lang : "auto";
  }

  /**
   * Explicitly render any not-yet-rendered `.cf-turnstile` widget in scope,
   * loading api.js first if needed. Guarded against double-render.
   */
  function renderTurnstile(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var pending = Array.prototype.filter.call(scope.querySelectorAll(".cf-turnstile"), function (el) {
      return !el.dataset.mfTsWidgetId && !el.querySelector("iframe");
    });
    if (!pending.length) return;
    ensureTurnstileApi(function () {
      if (!window.turnstile || typeof window.turnstile.render !== "function") return;
      pending.forEach(function (el) {
        if (el.dataset.mfTsWidgetId || el.querySelector("iframe")) return;
        try {
          // Only record a real widget id: assigning an undefined return would
          // store the string "undefined", which reads as rendered and would
          // stop the widget from ever being retried.
          var widgetId = window.turnstile.render(el, {language: turnstileLanguage(el)});
          if (widgetId) el.dataset.mfTsWidgetId = widgetId;
        } catch (e) {
          /* not ready / already rendered */
        }
      });
    });
  }

  function removeTurnstile(root) {
    if (!window.turnstile || typeof window.turnstile.remove !== "function") return;
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll(".cf-turnstile"), function (el) {
      if (!el.dataset.mfTsWidgetId) return;
      try {
        window.turnstile.remove(el.dataset.mfTsWidgetId);
      } catch (e) {
        /* already gone */
      }
      delete el.dataset.mfTsWidgetId;
    });
  }

  function init(root) {
    Array.prototype.forEach.call(document.querySelectorAll("[data-nova-contact-routing]"), function (form) {
      if (!form.dataset.novaContactRoutingReady) {
        form.dataset.novaContactRoutingReady = "true";
        form.novaContactRouting = new NovaContactRouting(form);
      }
    });
    renderTurnstile(root);
  }

  function destroy(root) {
    if (!root || !root.querySelectorAll) return;
    Array.prototype.forEach.call(root.querySelectorAll("[data-nova-contact-routing]"), function (form) {
      if (form.novaContactRouting) form.novaContactRouting.destroy();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor re-renders sections without a page load. Rebuild the widget in
  // the reloaded section, and tear it down when the section unloads.
  document.addEventListener("shopify:section:load", function (event) {
    init(event.target);
  });
  document.addEventListener("shopify:section:unload", function (event) {
    destroy(event.target);
    removeTurnstile(event.target);
  });

  if (typeof module !== "undefined") {
    module.exports = {
      formFitsViewport: formFitsViewport,
      NovaContactRouting: NovaContactRouting,
    };
  }
})();
